<template>
  <div id="invoicesPage">
    <InvoiceListComponent
      title="Invoices"
      :items="invoiceRows"
      :fields="invoicesFields"
      :loading="loading"
      :pagination="pagination"
      serverPagination
      @changePage="onChangePage"
      @changePageSize="onChangePageSize"
    >
      <template #header-actions>
        <div class="d-flex align-items-center">
          <CButton
            color="success"
            class="mr-3 d-flex align-items-center"
            :disabled="downloading || loading || pagination.total === 0"
            @click="onDownload"
          >
            <CSpinner v-if="downloading" size="sm" class="mr-1" />
            <CIcon v-else name="cil-cloud-download" class="mr-1" />
            Download
          </CButton>
          <CSelect
            style="width: 150px"
            class="mx-2 my-0"
            :value.sync="search.type"
            :options="search.options"
            @change="searchChangeType"
          />
          <CInput
            style="width: 320px"
            class="my-0 px-0"
            :placeholder="`Search ${search.placeholder}`"
            :value.sync="search.value"
            @keyup.enter.native="onSearch"
          >
            <template #append>
              <CButton color="primary" @click="onSearch">Search</CButton>
              <CButton
                class="ml-2"
                size="sm"
                color="info"
                variant="outline"
                @click="isShowFilter = !isShowFilter"
              >
                <CIcon :name="isShowFilter ? 'cil-x' : 'cil-arrow-bottom'" />
                Filter
              </CButton>
            </template>
          </CInput>
        </div>
      </template>

      <template #filter>
        <CCollapse :show="isShowFilter">
          <CCard class="p-3 mb-3">
            <CRow class="justify-content-between align-items-center mb-3 px-2">
              <h6 class="mb-0 font-weight-bold">Filter</h6>
              <div>
                <CButton
                  class="mr-2"
                  variant="outline"
                  color="info"
                  size="sm"
                  @click="doResetFilter"
                >
                  Reset
                </CButton>
                <CButton color="info" size="sm" @click="doApplyFilter">
                  Apply
                </CButton>
              </div>
            </CRow>
            <CRow>
              <CCol md="6">
                <label class="font-weight-bold mb-1">Type</label>
                <SelectAllTreeselect
                  v-model="filter.type"
                  :options="filterData.type"
                  placeholder="Select type"
                />
              </CCol>
              <CCol md="6">
                <label class="font-weight-bold mb-1">Created Date</label>
                <div>
                  <date-picker
                    v-model="filter.dateRangeSelected"
                    range
                    format="YYYY-MM-DD"
                    placeholder="Select created date"
                    class="w-100"
                  />
                </div>
              </CCol>
            </CRow>
          </CCard>
        </CCollapse>
      </template>
    </InvoiceListComponent>
    <vue-confirm-dialog></vue-confirm-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import InvoiceListComponent from '@/components/InvoiceListComponent';
import SelectAllTreeselect from '@/components/SelectAllTreeselect.vue';
import InvoiceProxy from '@/proxies/invoice.proxy';
import { ACTION_FETCH_INVOICES_PAGING } from '@/store/actions.type';

export default {
  name: 'Invoices',
  components: {
    InvoiceListComponent,
    SelectAllTreeselect,
  },
  data() {
    return {
      loading: false,
      downloading: false,
      isShowFilter: false,
      requestSequence: 0,
      invoiceRows: [],
      pagination: {
        page: 1,
        limit: 25,
        total: 0,
      },
      search: {
        type: 'invoice_no',
        value: '',
        placeholder: 'Invoice no',
        options: [
          { value: 'invoice_no', label: 'Invoice no' },
          { value: 'booking_no', label: 'Booking no' },
        ],
      },
      filter: {
        type: null,
        dateRangeSelected: null,
      },
      filterData: {
        type: [
          { id: 'sale', label: 'Sale' },
          { id: 'tax', label: 'Tax' },
          { id: 'payout', label: 'Payout' },
        ],
      },
      invoicesFields: [
        {
          key: 'invoice_no',
          label: 'InvoiceNo',
        },
        {
          key: 'booking_no',
          label: 'BookingNo',
        },
        {
          key: 'issued_by',
          label: 'IssuedBy',
        },
        {
          key: 'invoice_type',
          label: 'Type',
        },
        {
          key: 'pdf_file',
          label: 'PDF',
        },
        {
          key: 'created_at',
          label: 'CreatedDate',
        },
      ],
    };
  },
  mounted() {
    this.applyRoutePagination();
    this.fetchInvoices();
  },
  watch: {
    $route(route) {
      if (this.applyRoutePagination(route.query)) {
        this.fetchInvoices();
      }
    },
  },
  methods: {
    searchChangeType() {
      const selected = this.search.options.find(
        (x) => x.value === this.search.type
      );
      this.search.placeholder = selected ? selected.label : '';
      this.search.value = '';
    },
    buildQuery(forDownload = false) {
      const query = {
        issued_by: 'comenow',
      };

      if (!forDownload) {
        query.page = this.pagination.page;
        query.limit = this.pagination.limit;
      }

      if (this.search.value && this.search.value.trim().length > 0) {
        const val = this.search.value.trim();
        if (this.search.type === 'invoice_no') {
          query.invoice_no = val;
        } else if (this.search.type === 'booking_no') {
          query.booking_no = val;
        }
      }

      if (this.filter.type && this.filter.type.length > 0) {
        query.invoice_type = this.filter.type;
      }

      if (
        this.filter.dateRangeSelected &&
        this.filter.dateRangeSelected.length === 2 &&
        this.filter.dateRangeSelected[0] &&
        this.filter.dateRangeSelected[1]
      ) {
        query.from = moment(this.filter.dateRangeSelected[0]).format('YYYY-MM-DD');
        query.to = moment(this.filter.dateRangeSelected[1]).format('YYYY-MM-DD');
      }

      return query;
    },
    applyRoutePagination(routeQuery) {
      const query =
        routeQuery ||
        (this.$route && this.$route.query ? this.$route.query : {});
      const page = Number(query.page);
      const limit = Number(query.limit);
      const nextPage = Number.isInteger(page) && page > 0 ? page : 1;
      const nextLimit = [5, 10, 25, 50].includes(limit) ? limit : 25;
      const changed =
        nextPage !== this.pagination.page ||
        nextLimit !== this.pagination.limit;
      this.pagination.page = nextPage;
      this.pagination.limit = nextLimit;
      return changed;
    },
    updateURL() {
      if (!this.$router) return Promise.resolve();
      return this.$router
        .push({
          query: {
            page: this.pagination.page,
            limit: this.pagination.limit,
          },
        })
        .catch(() => {});
    },
    async fetchInvoices() {
      const requestSequence = ++this.requestSequence;
      this.loading = true;
      try {
        const query =
          (this.buildQuery ? this.buildQuery(false) : null) || {
            issued_by: 'comenow',
            page: this.pagination.page,
            limit: this.pagination.limit,
          };
        const payload = await this.$store.dispatch(
          ACTION_FETCH_INVOICES_PAGING,
          query
        );
        if (requestSequence !== this.requestSequence) {
          return;
        }
        const paging = payload || {};
        this.invoiceRows = Array.isArray(paging.rows) ? paging.rows : [];
        this.pagination = {
          page: Number(paging.page) || this.pagination.page,
          limit: Number(paging.limit) || this.pagination.limit,
          total: Number(paging.total_rows) || 0,
        };
      } catch (error) {
        if (requestSequence === this.requestSequence) {
          this.$toast.error('Failed to load invoices');
        }
      } finally {
        if (requestSequence === this.requestSequence) {
          this.loading = false;
        }
      }
    },
    onSearch() {
      this.pagination.page = 1;
      this.updateURL();
      return this.fetchInvoices();
    },
    doApplyFilter() {
      this.pagination.page = 1;
      this.updateURL();
      return this.fetchInvoices();
    },
    doResetFilter() {
      this.search.value = '';
      this.filter.type = null;
      this.filter.dateRangeSelected = null;
      this.pagination.page = 1;
      this.updateURL();
      return this.fetchInvoices();
    },
    async onChangePage(page) {
      this.pagination.page = page;
      await this.updateURL();
      return this.fetchInvoices();
    },
    async onChangePageSize(limit) {
      this.pagination.limit = limit;
      this.pagination.page = 1;
      await this.updateURL();
      return this.fetchInvoices();
    },
    onDownload() {
      const count =
        Number(this.pagination.total) ||
        (this.invoiceRows ? this.invoiceRows.length : 0);
      if (count === 0) {
        if (this.$toast) {
          this.$toast.warning('No invoices found to download');
        }
        return;
      }

      const message = `There are ${count} invoice file(s) that will be downloaded. Do you want to proceed?`;
      if (typeof this.$confirm === 'function') {
        this.$confirm({
          title: 'Confirm Download',
          message,
          button: {
            no: 'Cancel',
            yes: 'OK',
          },
          callback: (confirm) => {
            if (confirm) {
              this.executeDownload();
            }
          },
        });
      } else {
        this.executeDownload();
      }
    },
    async executeDownload() {
      if (this.downloading) return;
      this.downloading = true;
      try {
        const query = this.buildQuery ? this.buildQuery(true) : { issued_by: 'comenow' };
        const res = await InvoiceProxy.downloadZip(query);
        let blob;
        if (res instanceof Blob) {
          blob = res;
        } else if (res && res.data instanceof Blob) {
          blob = res.data;
        } else if (res && res.data) {
          blob = new Blob([res.data], { type: 'application/zip' });
        } else if (res) {
          blob = new Blob([res], { type: 'application/zip' });
        }

        if (!blob || blob.size <= 22) {
          let errorMsg = 'No invoice files could be downloaded';
          if (blob && typeof blob.text === 'function') {
            try {
              const textContent = await blob.text();
              if (textContent === 'undefined') {
                errorMsg = 'Please refresh your browser (Cmd + Shift + R) to reload the latest version';
              } else {
                const json = JSON.parse(textContent);
                if (json && json.message) {
                  errorMsg = json.message;
                }
              }
            } catch (parseErr) {
              console.debug('Failed to parse error blob text', parseErr);
            }
          }
          throw new Error(errorMsg);
        }

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        const timestamp = moment().format('YYYYMMDD_HHmmss');
        link.setAttribute('download', `invoices_${timestamp}.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        if (this.$toast) {
          this.$toast.success('Invoices downloaded successfully');
        }
      } catch (err) {
        if (this.$toast) {
          let errorMsg = err.message || 'Failed to download invoices';
          if (err && err.response && err.response.data) {
            if (typeof err.response.data.text === 'function') {
              try {
                const text = await err.response.data.text();
                const json = JSON.parse(text);
                if (json && json.message) {
                  errorMsg = json.message;
                }
              } catch (parseErr) {
                console.debug('Failed to parse error blob', parseErr);
              }
            } else if (err.response.data.message) {
              errorMsg = err.response.data.message;
            }
          }
          this.$toast.error(errorMsg);
        }
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style></style>
